// 당일 순위 + 팀전 MVP 계산 — 순수 함수.
//
// matches/participants/teams를 받아 leaderboard rows와 (해당되면) MVP를
// 만든다. 정렬 규칙: 승률 → 승수 → 득실 → 경기 수.
//
// 교류전 호스트팀 규약: friendly match는 team_order=1이 호스트 클럽.
// 우리(=호스트) 팀이 진 교류전에서는 MVP를 숨겨 상대 클럽 선수 축하를
// 억제한다. 내부 팀전이면 항상 MVP 노출.

import { matchPlayerName } from "../matches";

export interface RankingRow {
  key: string;
  label: string;
  wins: number;
  losses: number;
  draws: number;
  scored: number;
  conceded: number;
  total: number;
  decided: number;
  diff: number;
}

export interface MvpInfo {
  name: string;
  teamLabel: string;
  record: string;
  rate: string;
  diff: number;
}

export interface EventRanking {
  isTeam: boolean;
  rows: RankingRow[];
  mvp: MvpInfo | null;
}

interface RawStats {
  wins: number;
  losses: number;
  draws: number;
  scored: number;
  conceded: number;
}

interface PlayerStats extends RawStats {
  participant: any;
}

function emptyStats(): RawStats {
  return { wins: 0, losses: 0, draws: 0, scored: 0, conceded: 0 };
}

export function computeEventRanking(
  ev: any,
  matches: any[],
  participants: any[],
  teams: any[],
): EventRanking | null {
  const decided = matches.filter((m) => m.winner_side);
  if (decided.length === 0) return null;

  const isTeam = ev.mode === "team";
  const stats = new Map<string, RawStats>();
  const labelOf = new Map<string, string>();
  const playerStats = new Map<string, PlayerStats>();

  if (isTeam) {
    for (const t of teams) {
      stats.set(t.team_id, emptyStats());
      labelOf.set(t.team_id, t.team_name || `팀 ${t.team_order || ""}`);
    }
  } else {
    for (const p of participants) {
      stats.set(p.event_participant_id, emptyStats());
      labelOf.set(p.event_participant_id, matchPlayerName(p));
    }
  }
  for (const p of participants) {
    playerStats.set(p.event_participant_id, { participant: p, ...emptyStats() });
  }

  for (const m of decided) {
    const sideA = (m.sides || []).find((s: any) => s.side_number === 1);
    const sideB = (m.sides || []).find((s: any) => s.side_number === 2);
    if (!sideA || !sideB) continue;
    const scoreA = Number(m.side_a_score) || 0;
    const scoreB = Number(m.side_b_score) || 0;
    const keysA: string[] = isTeam ? (sideA.team_id ? [sideA.team_id] : []) : (sideA.player_ids || []);
    const keysB: string[] = isTeam ? (sideB.team_id ? [sideB.team_id] : []) : (sideB.player_ids || []);
    const pidsA: string[] = sideA.player_ids || [];
    const pidsB: string[] = sideB.player_ids || [];
    const bump = <T extends RawStats>(map: Map<string, T>, keys: string[], field: keyof RawStats) =>
      keys.forEach((k) => {
        const s = map.get(k);
        if (s) (s[field] as number)++;
      });
    const addScore = <T extends RawStats>(map: Map<string, T>, keys: string[], s: number, c: number) =>
      keys.forEach((k) => {
        const r = map.get(k);
        if (r) {
          r.scored += s;
          r.conceded += c;
        }
      });
    // 팀전은 서로 다른 팀 간 경기만 MVP 후보 개인 기록에 반영.
    // 같은 팀끼리·cross_team_mixed(team_id=None)는 제외.
    const mvpEligible = !isTeam || (keysA.length > 0 && keysB.length > 0 && keysA[0] !== keysB[0]);
    if (m.winner_side === "side_a") {
      bump(stats, keysA, "wins");
      bump(stats, keysB, "losses");
      if (mvpEligible) {
        bump(playerStats, pidsA, "wins");
        bump(playerStats, pidsB, "losses");
      }
    } else if (m.winner_side === "side_b") {
      bump(stats, keysB, "wins");
      bump(stats, keysA, "losses");
      if (mvpEligible) {
        bump(playerStats, pidsB, "wins");
        bump(playerStats, pidsA, "losses");
      }
    } else if (m.winner_side === "draw") {
      bump(stats, keysA, "draws");
      bump(stats, keysB, "draws");
      if (mvpEligible) {
        bump(playerStats, pidsA, "draws");
        bump(playerStats, pidsB, "draws");
      }
    }
    addScore(stats, keysA, scoreA, scoreB);
    addScore(stats, keysB, scoreB, scoreA);
    if (mvpEligible) {
      addScore(playerStats, pidsA, scoreA, scoreB);
      addScore(playerStats, pidsB, scoreB, scoreA);
    }
  }

  const rows: RankingRow[] = [...stats.entries()]
    .map(([key, s]) => ({
      key,
      label: labelOf.get(key) || "-",
      wins: s.wins,
      losses: s.losses,
      draws: s.draws,
      scored: s.scored,
      conceded: s.conceded,
      total: s.wins + s.losses + s.draws,
      decided: s.wins + s.losses,
      diff: s.scored - s.conceded,
    }))
    .filter((r) => r.total > 0)
    .sort((a, b) => {
      const ra = a.decided ? a.wins / a.decided : 0;
      const rb = b.decided ? b.wins / b.decided : 0;
      if (rb !== ra) return rb - ra;
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (b.diff !== a.diff) return b.diff - a.diff;
      return b.total - a.total;
    });

  if (rows.length === 0) return null;

  const mvp = computeTeamMvp(ev, rows, teams, playerStats, isTeam);
  return { isTeam, rows, mvp };
}

function computeTeamMvp(
  ev: any,
  rows: RankingRow[],
  teams: any[],
  playerStats: Map<string, PlayerStats>,
  isTeam: boolean,
): MvpInfo | null {
  if (!isTeam || rows.length === 0 || ev.status !== "completed") return null;
  const winningKey = rows[0].key;
  const winningTeam = (teams || []).find((t) => t.team_id === winningKey);
  if (!winningTeam) return null;
  const isFriendly = !!ev.is_friendly_match;
  const winnerIsHost = winningTeam.team_order === 1;
  if (isFriendly && !winnerIsHost) return null;
  const candidatePids: string[] = winningTeam.participant_ids || [];
  const ranked = candidatePids
    .map((pid) => playerStats.get(pid))
    .filter((ps): ps is PlayerStats => !!ps && ps.wins + ps.losses + ps.draws > 0)
    .sort((a, b) => {
      const ad = a.wins + a.losses;
      const bd = b.wins + b.losses;
      const ra = ad ? a.wins / ad : 0;
      const rb = bd ? b.wins / bd : 0;
      if (rb !== ra) return rb - ra;
      if (b.wins !== a.wins) return b.wins - a.wins;
      return b.scored - b.conceded - (a.scored - a.conceded);
    });
  const top = ranked[0];
  if (!top) return null;
  const dec = top.wins + top.losses;
  const rate = dec > 0 ? `${Math.round((top.wins / dec) * 100)}%` : "-";
  const record =
    top.draws > 0
      ? `${top.wins}승 ${top.draws}무 ${top.losses}패`
      : `${top.wins}승 ${top.losses}패`;
  return {
    name: matchPlayerName(top.participant),
    teamLabel: winningTeam.team_name || `팀 ${winningTeam.team_order || ""}`,
    record,
    rate,
    diff: top.scored - top.conceded,
  };
}
